import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch, isSignedIn } from "./api";

// An admin can own more than one restaurant (small chains, multi-location
// owners). This context is the single source of truth for "which restaurant
// is currently selected" so every page (Reviews, Stats, Users, Invites,
// Restaurant settings) scopes its data to the same one, instead of each page
// picking independently and risking one showing location A while another
// shows location B.
type RestaurantSummary = {
  id: number;
  name: string;
  // Only ever present for a manager caller (Fred, 2026-09-15: delegated
  // permissions). Admins bypass these checks by ownership, so the backend
  // never bothers attaching this for them.
  canManageQr?: boolean;
  canEditLandingPage?: boolean;
  canViewAnalytics?: boolean;
  canOrderCards?: boolean;
};

/** Mirrors the backend's BillingState. "none" means no trial, no subscription. */
export type BillingState =
  | "trialing" | "trial_expired" | "active"
  | "past_due" | "past_due_expired" | "canceled" | "none";

/**
 * Which states still permit writing. Deliberately the same predicate as the
 * server's billingAllowsWrites: the UI disabling a button is a courtesy, the
 * API is the control, and they must agree or the app lies to the user.
 */
export function stateAllowsWrites(state?: BillingState | null): boolean {
  return state === "active" || state === "trialing" || state === "past_due";
}

type RestaurantContextValue = {
  restaurants: RestaurantSummary[];
  currentRestaurantId: number | null;
  selectRestaurant: (id: number) => void;
  refreshRestaurants: () => Promise<void>;
  loading: boolean;
  /** Billing state per restaurant id, from /api/billing/status. */
  billing: Record<number, BillingState>;
  /** Whether the *currently selected* location accepts writes. */
  canWrite: boolean;
  /** State of the currently selected location, for the banner's wording. */
  currentState: BillingState | null;
  refreshBilling: () => Promise<void>;
  // Delegated manager permissions at the currently selected location,
  // already true for admins (who pass by ownership, not delegation) so
  // every consumer can just check the context value directly.
  canManageQr: boolean;
  canEditLandingPage: boolean;
  canViewAnalytics: boolean;
  canOrderCards: boolean;
};

const STORAGE_KEY = "rc_currentRestaurantId";

const RestaurantContext = createContext<RestaurantContextValue>({
  restaurants: [],
  currentRestaurantId: null,
  selectRestaurant: () => {},
  refreshRestaurants: async () => {},
  loading: true,
  billing: {},
  // Optimistic until the first fetch resolves: showing a read-only warning for
  // a split second on every page load, to every paying customer, would be a
  // worse bug than the one this feature fixes.
  canWrite: true,
  currentState: null,
  refreshBilling: async () => {},
  canManageQr: false,
  canEditLandingPage: false,
  canViewAnalytics: false,
  canOrderCards: false,
});

export const useRestaurant = () => useContext(RestaurantContext);

function getStoredUser(): any {
  return JSON.parse(localStorage.getItem("user") || "{}");
}

// Picks the selection to use: the previously-selected restaurant if it's
// still in the list, otherwise the first one (lowest id, same restaurant
// login puts in user.restaurantId), otherwise null.
function resolveSelection(restaurants: RestaurantSummary[], stored: number | null): number | null {
  if (stored !== null && restaurants.some((r) => r.id === stored)) return stored;
  return restaurants[0]?.id ?? null;
}

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = getStoredUser();
  const isAdmin = user.role === "admin";
  // Managers can hold several locations since S12, so they need the list and
  // the switcher too, not just admins.
  const hasLocations = isAdmin || user.role === "manager";
  const managerRestaurantId: number | null =
    user.role === "manager" ? user.managerRestaurantId ?? null : null;

  const [restaurants, setRestaurants] = useState<RestaurantSummary[]>(
    hasLocations && Array.isArray(user.restaurants) ? user.restaurants : []
  );
  const [currentRestaurantId, setCurrentRestaurantId] = useState<number | null>(() => {
    if (!hasLocations) return managerRestaurantId;
    // A link from a new-review email or notification names the location
    // (?restaurantId=). Only honoured if it is one of the user's own,
    // which resolveSelection checks.
    const linked = Number(new URLSearchParams(window.location.search).get("restaurantId"));
    const stored = linked || Number(localStorage.getItem(STORAGE_KEY));
    return resolveSelection(
      Array.isArray(user.restaurants) ? user.restaurants : [],
      isNaN(stored) || !stored ? null : stored
    );
  });
  const [loading, setLoading] = useState(hasLocations);
  const [billing, setBilling] = useState<Record<number, BillingState>>({});

  const refreshBilling = useCallback(async () => {
    // RestaurantProvider wraps the whole app, including public routes like
    // /login, and an anonymous visitor has no session to check billing for, and
    // the 401 this would otherwise draw used to retrigger a reload on /login,
    // which remounted this provider and looped (2026-09-14 incident).
    if (!isSignedIn()) return;
    // Superadmin is never restricted, and the endpoint would hand back every
    // restaurant on the platform, not something to hold in this context.
    if (user.role === "superadmin") return;
    try {
      const res = await apiFetch("/api/billing/status");
      if (!res?.ok) return;
      const rows = await res.json();
      const next: Record<number, BillingState> = {};
      for (const r of rows) next[r.id] = r.state;
      setBilling(next);
    } catch {
      // Leave the previous map in place. Failing open is right here: the API
      // still enforces the real rule, and a network blip must not spuriously
      // tell a paying customer their account is restricted.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  useEffect(() => { refreshBilling(); }, [refreshBilling]);

  const refreshRestaurants = useCallback(async () => {
    if (!hasLocations) {
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch("/api/restaurants/my");
      const list: RestaurantSummary[] =
        res && res.ok
          ? (await res.json()).map((r: any) => ({
              id: r.id, name: r.name,
              canManageQr: r.managerPermissions?.canManageQr,
              canEditLandingPage: r.managerPermissions?.canEditLandingPage,
              canViewAnalytics: r.managerPermissions?.canViewAnalytics,
              canOrderCards: r.managerPermissions?.canOrderCards,
            }))
          : [];
      setRestaurants(list);

      // Keep localStorage's user.restaurants in sync so a page reload (which
      // seeds this context from localStorage before this fetch resolves)
      // still has an up to date list without needing to log in again.
      const storedUser = getStoredUser();
      localStorage.setItem("user", JSON.stringify({ ...storedUser, restaurants: list }));

      setCurrentRestaurantId((prev) => resolveSelection(list, prev));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLocations]);

  useEffect(() => {
    refreshRestaurants();
  }, [refreshRestaurants]);

  const selectRestaurant = (id: number) => {
    setCurrentRestaurantId(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  };

  const effectiveId = currentRestaurantId ?? managerRestaurantId;
  const currentState = effectiveId ? billing[effectiveId] ?? null : null;
  const currentRestaurant = restaurants.find((r) => r.id === effectiveId);

  return (
    <RestaurantContext.Provider
      value={{
        restaurants,
        currentRestaurantId: effectiveId,
        selectRestaurant,
        refreshRestaurants,
        loading,
        billing,
        // Unknown state means "allow": the server is the control, and blocking
        // on missing data would lock out a paying customer over a failed fetch.
        canWrite: currentState === null ? true : stateAllowsWrites(currentState),
        currentState,
        refreshBilling,
        // Admins always pass (they get here by ownership, not delegation) so
        // every consumer can check the context value alone. The API is still
        // the actual control either way.
        canManageQr: isAdmin || !!currentRestaurant?.canManageQr,
        canEditLandingPage: isAdmin || !!currentRestaurant?.canEditLandingPage,
        canViewAnalytics: isAdmin || !!currentRestaurant?.canViewAnalytics,
        canOrderCards: isAdmin || !!currentRestaurant?.canOrderCards,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};
